import { forwardRef } from 'react';
import { clsx } from 'clsx';
import type { ButtonProps } from '@/types/ui';

/**
 * Webcoda-styled Button component
 * Supports multiple variants, sizes, and states following design system
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  fullWidth = false,
  icon,
  iconPosition = 'left',
  className,
  children,
  'data-testid': testId,
  ...props
}, ref) => {
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'rounded-lg',
    'font-medium',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'dark:focus:ring-offset-gray-800',
    'disabled:opacity-50',
    'disabled:pointer-events-none',
    'disabled:cursor-not-allowed',
  ];

  const variantClasses = {
    primary: [
      'bg-primary-500',
      'text-white',
      'hover:bg-primary-600',
      'focus:ring-primary-500',
      'shadow-soft',
      'hover:shadow-medium',
      'active:bg-primary-700',
    ],
    secondary: [
      'bg-secondary-500',
      'text-white',
      'hover:bg-secondary-600',
      'focus:ring-secondary-500',
      'shadow-soft',
      'hover:shadow-medium',
      'active:bg-secondary-700',
    ],
    outline: [
      'border',
      'border-gray-300',
      'dark:border-gray-600',
      'bg-white',
      'dark:bg-gray-800',
      'text-gray-700',
      'dark:text-gray-300',
      'hover:bg-gray-50',
      'dark:hover:bg-gray-700',
      'focus:ring-gray-500',
      'hover:border-gray-400',
      'dark:hover:border-gray-500',
      'active:bg-gray-100',
      'dark:active:bg-gray-600',
    ],
    ghost: [
      'text-gray-600',
      'dark:text-gray-400',
      'hover:text-gray-900',
      'dark:hover:text-gray-100',
      'hover:bg-gray-100',
      'dark:hover:bg-gray-700',
      'focus:ring-gray-500',
      'active:bg-gray-200',
      'dark:active:bg-gray-600',
    ],
    danger: [
      'bg-error-500',
      'text-white',
      'hover:bg-error-600',
      'focus:ring-error-500',
      'shadow-soft',
      'hover:shadow-medium',
      'active:bg-error-700',
    ],
  };

  const sizeClasses = {
    sm: ['px-3', 'py-1.5', 'text-xs', 'min-h-[32px]'],
    md: ['px-4', 'py-2', 'text-sm', 'min-h-[40px]'],
    lg: ['px-6', 'py-3', 'text-base', 'min-h-[48px]'],
  };

  const widthClasses = fullWidth ? ['w-full'] : [];

  const loadingClasses = loading ? ['cursor-wait'] : [];

  const classes = clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    widthClasses,
    loadingClasses,
    className
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !loading && onClick) {
      onClick(event);
    }
    // Don't prevent default for submit buttons - let them trigger form submission
  };

  const renderIcon = () => {
    if (loading) {
      return (
        <div 
          className={clsx(
            'animate-spin rounded-full border-2 border-current border-t-transparent',
            size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4',
            children && iconPosition === 'left' ? 'mr-2' : children && iconPosition === 'right' ? 'ml-2' : ''
          )}
          aria-hidden="true"
        />
      );
    }

    if (icon) {
      return (
        <span 
          className={clsx(
            'flex items-center justify-center',
            children && iconPosition === 'left' ? 'mr-2' : children && iconPosition === 'right' ? 'ml-2' : '',
            size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-lg' : 'text-sm'
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
      );
    }

    return null;
  };

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || loading}
      onClick={handleClick}
      data-testid={testId}
      aria-busy={loading}
      {...props}
    >
      {iconPosition === 'left' && renderIcon()}
      
      {children && (
        <span className={loading && !icon ? 'opacity-0' : undefined}>
          {children}
        </span>
      )}
      
      {iconPosition === 'right' && renderIcon()}
    </button>
  );
});

Button.displayName = 'Button';